import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, BookOpen, Search } from "lucide-react";
import { format } from "date-fns";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { BLOG_POSTS } from "@/data/blog-posts";
import { supabase } from "@/integrations/supabase/client";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";

export const Route = createFileRoute("/blog")({
  component: BlogPage,
  head: () => ({
    meta: [
      { title: "SimpleTech Insights Blog — IT, Cybersecurity & Web Tips" },
      {
        name: "description",
        content:
          "Expert tips, industry news, and insights into the world of IT, cybersecurity, and small business technology.",
      },
      { property: "og:title", content: "SimpleTech Insights Blog" },
      {
        property: "og:description",
        content: "Expert tips, industry news, and insights into the world of IT and security.",
      },
      seoOgUrl("/blog"),
    ],
    links: [seoCanonicalLink("/blog")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "SimpleTech Insights Blog",
          description:
            "Expert tips, industry news, and insights into the world of IT, cybersecurity, and small business technology.",
          url: canonical("/blog"),
          publisher: {
            "@type": "Organization",
            name: "SimpleTech",
            url: SITE_URL,
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Blog", item: canonical("/blog") },
          ],
        }),
      },
    ],
  }),
});

const FILTERS = ["All", "Technology", "Security", "Cyber Security", "AI", "ADA Website Compliance", "Software Integration", "Web Design", "Managed IT", "VoIP", "Senior Tech Support", "Tech Support"] as const;
type Filter = (typeof FILTERS)[number];

type UnifiedPost = {
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  isoDate: string;
  category: Filter;
  coverImage?: string;
  source: "cms" | "static";
};

function BlogPage() {
  const [filter, setFilter] = useState<Filter>("All");
  const [search, setSearch] = useState("");

  const queryClient = useQueryClient();
  const { data: cmsPosts } = useQuery({
    queryKey: ["public", "posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posts")
        .select("id, title, slug, excerpt, featured_image, published_at, categories(name)")
        .eq("status", "published")
        .lte("published_at", new Date().toISOString())
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const channel = supabase
      .channel("public-posts-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "posts" }, () => {
        queryClient.invalidateQueries({ queryKey: ["public", "posts"] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const cmsUnified: UnifiedPost[] = (cmsPosts ?? []).map((p) => {
    const catName = p.categories?.name ?? "Technology";
    const category: Filter = (FILTERS as readonly string[]).includes(catName)
      ? (catName as Filter)
      : "Technology";
    return {
      slug: p.slug,
      title: p.title,
      excerpt: p.excerpt ?? "",
      author: "SimpleTech",
      date: p.published_at ? format(new Date(p.published_at), "PP") : "",
      isoDate: p.published_at ?? new Date().toISOString(),
      category,
      coverImage: p.featured_image ?? undefined,
      source: "cms",
    };
  });

  const staticUnified: UnifiedPost[] = BLOG_POSTS.map((p) => ({
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt,
    author: p.author,
    date: p.date,
    isoDate: p.isoDate,
    category: p.category,
    coverImage: p.coverImage,
    source: "static",
  }));

  const allPosts = [...cmsUnified, ...staticUnified].sort((a, b) =>
    b.isoDate.localeCompare(a.isoDate),
  );

  const filteredPosts = allPosts.filter((post) => {
    const matchesCategory = filter === "All" || post.category === filter;
    const matchesSearch =
      !search.trim() ||
      [post.title, post.excerpt, post.category, post.author].some((field) =>
        field.toLowerCase().includes(search.trim().toLowerCase()),
      );
    return matchesCategory && matchesSearch;
  });

  // Featured is only shown when filter is "All" and it has an image
  const featured = filter === "All" ? filteredPosts.find((p) => !!p.coverImage) : undefined;
  const gridPosts = featured
    ? filteredPosts.filter((p) => !(p.source === featured.source && p.slug === featured.slug))
    : filteredPosts;

  return (
    <div className="min-h-screen bg-slate-50 text-[#0b0a3d] flex flex-col">
      <SiteHeader active="blog" />
      <main id="main-content" className="flex-1">
        {/* HERO — dark navy */}
        <section className="bg-[#0b0a3d] pt-20 pb-32 md:pt-24 md:pb-40 px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <span className="inline-flex items-center gap-2 text-sky-400 font-semibold tracking-wider uppercase text-xs">
              <BookOpen className="w-3.5 h-3.5" /> The SimpleTech Blog
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mt-4 leading-tight tracking-tight">
              Insights for a smarter, more secure digital world
            </h1>
            <p className="text-slate-300 mt-6 text-lg max-w-2xl mx-auto leading-relaxed">
              Expert advice on managed IT, cybersecurity, and the future of business technology.
            </p>
          </div>
        </section>

        {/* FEATURED — overlaps hero */}
        {featured && (
          <section className="px-6 -mt-20 md:-mt-24">
            <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row">
              <div className="md:w-3/5 bg-slate-100 min-h-[260px] md:min-h-[380px]">
                {featured.coverImage && (
                  <img
                    src={featured.coverImage}
                    alt={featured.title}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                )}
              </div>
              <div className="md:w-2/5 p-8 md:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-3 py-1 bg-sky-100 text-sky-600 text-xs font-bold rounded-full uppercase tracking-wider">
                    {featured.category}
                  </span>
                  <span className="text-slate-400 text-sm">{featured.date}</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold leading-tight text-[#0b0a3d]">
                  {featured.title}
                </h2>
                <p className="text-slate-600 mt-4 line-clamp-3 leading-relaxed">
                  {featured.excerpt}
                </p>
                <div className="mt-8">
                  <Link
                    to="/articles/$slug"
                    params={{ slug: featured.slug }}
                    aria-label={`Read article: ${featured.title}`}
                    className="inline-flex items-center gap-2 bg-[#0b0a3d] hover:bg-[#161560] text-white font-bold text-sm px-6 py-3 rounded-lg transition-colors"
                  >
                    Read article
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* SEARCH + CATEGORIES */}
        <section className="max-w-6xl mx-auto px-6 pt-16 md:pt-20">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-slate-200 pb-8">
              <div>
                <h3 className="text-2xl md:text-3xl font-extrabold text-[#0b0a3d] tracking-tight">
                  Latest articles
                </h3>
                <p className="text-slate-500 text-sm mt-1">
                  {filteredPosts.length} {filteredPosts.length === 1 ? "post" : "posts"}
                  {filter !== "All" ? ` in ${filter}` : ""}
                </p>
              </div>
              <div className="relative w-full md:w-96">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition-all"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => {
                const active = filter === f;
                return (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={
                      active
                        ? "px-4 py-2 rounded-full bg-[#0b0a3d] text-white text-sm font-semibold transition-colors"
                        : "px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-sky-500 hover:text-sky-600 text-sm font-medium transition-colors"
                    }
                  >
                    {f}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* POST GRID */}
        <section className="max-w-6xl mx-auto px-6 pt-12 pb-24">
          {gridPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">
                No posts found{search ? ` matching "${search}"` : ""}.
              </p>
              <button
                onClick={() => {
                  setSearch("");
                  setFilter("All");
                }}
                className="mt-4 text-sky-500 font-semibold hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {gridPosts.map((post) => (
                <Link
                  key={`${post.source}-${post.slug}`}
                  to="/articles/$slug"
                  params={{ slug: post.slug }}
                  className="group bg-white rounded-xl overflow-hidden border border-slate-100 hover:border-sky-500/50 hover:shadow-xl transition-all flex flex-col"
                >
                  {post.coverImage ? (
                    <div className="aspect-video bg-slate-100 overflow-hidden">
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-sky-100 via-white to-slate-50 flex items-center justify-center">
                      <span className="text-sky-500/60 text-xs font-bold uppercase tracking-widest">
                        {post.category}
                      </span>
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-sky-600 text-xs font-bold uppercase tracking-wider">
                      {post.category}
                    </span>
                    <h4 className="text-lg font-bold mt-2 leading-snug text-[#0b0a3d] group-hover:text-sky-500 transition-colors">
                      {post.title}
                    </h4>
                    <p className="mt-3 text-sm text-slate-500 leading-relaxed line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="mt-6 pt-6 border-t border-slate-50 flex items-center justify-between">
                      <span className="text-slate-400 text-xs">{post.date}</span>
                      <span className="text-[#0b0a3d] text-sm font-bold flex items-center gap-1 group-hover:text-sky-500 transition-colors">
                        Read more
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* CTA — dark navy */}
        <section className="px-6 pb-24">
          <div className="max-w-6xl mx-auto bg-[#0b0a3d] rounded-3xl p-12 md:p-16 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500/10 rounded-full -mr-32 -mt-32 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-sky-500/10 rounded-full -ml-32 -mb-32 blur-3xl" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-extrabold mb-5 tracking-tight">
                Ready to transform your IT?
              </h2>
              <p className="text-slate-300 mb-8 max-w-xl mx-auto leading-relaxed">
                Schedule a free strategy call today and see the difference SimpleTech can make for
                your business.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white font-bold text-sm tracking-wider px-8 py-4 rounded-xl transition-colors"
              >
                Get started
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
